import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft, ChevronRight, CheckCircle, FileText, ClipboardList, 
  Users, X, HelpCircle, Download, Plus, Trash2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TEMPLATE_ICONS = {
  impact_assessment: ClipboardList,
  change_plan: FileText,
  stakeholder_pack: Users,
};

export default function GuidedMode({ projectId, onClose, onAskCoach }) {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [session, setSession] = useState(null);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [formData, setFormData] = useState({});
  const [repeatableItems, setRepeatableItems] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Fetch templates on mount
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await axios.get(`${API}/templates`);
        setTemplates(response.data);
      } catch (error) {
        console.error('Error fetching templates:', error);
        toast.error('Failed to load templates');
      } finally {
        setLoading(false);
      }
    };
    fetchTemplates();
  }, []);

  // Start a guided session
  const startSession = async (templateId) => {
    try {
      setLoading(true);
      // Get template details
      const templateRes = await axios.get(`${API}/templates/${templateId}`);
      setSelectedTemplate(templateRes.data);
      
      // Create session
      const sessionRes = await axios.post(`${API}/guided-sessions`, {
        template_id: templateId,
        project_id: projectId
      });
      setSession(sessionRes.data);
      setFormData(sessionRes.data.data || {});
      setCurrentSectionIndex(sessionRes.data.current_section || 0);
      
      // Initialize repeatable items from saved data
      const repeatables = {};
      templateRes.data.sections.forEach(section => {
        if (section.repeatable) {
          repeatables[section.id] = sessionRes.data.data?.[section.id] || [{}];
        }
      });
      setRepeatableItems(repeatables);
      
    } catch (error) {
      console.error('Error starting session:', error);
      toast.error('Failed to start guided session');
    } finally {
      setLoading(false);
    }
  };

  // Save progress
  const saveProgress = async (newData = formData, newSection = currentSectionIndex) => {
    if (!session) return;
    
    setSaving(true);
    try {
      await axios.patch(`${API}/guided-sessions/${session.id}`, {
        current_section: newSection,
        data: newData
      });
    } catch (error) {
      console.error('Error saving progress:', error);
    } finally {
      setSaving(false);
    }
  };

  // Handle field change
  const handleFieldChange = (sectionId, fieldId, value, itemIndex = null) => {
    if (itemIndex !== null) {
      // Repeatable field
      const items = [...(repeatableItems[sectionId] || [{}])];
      items[itemIndex] = { ...items[itemIndex], [fieldId]: value };
      setRepeatableItems({ ...repeatableItems, [sectionId]: items });
      
      const newData = { ...formData, [sectionId]: items };
      setFormData(newData);
    } else {
      const newData = { ...formData, [sectionId]: { ...formData[sectionId], [fieldId]: value } };
      setFormData(newData);
    }
  };

  // Add repeatable item
  const addRepeatableItem = (sectionId) => {
    const items = [...(repeatableItems[sectionId] || []), {}];
    setRepeatableItems({ ...repeatableItems, [sectionId]: items });
    const newData = { ...formData, [sectionId]: items };
    setFormData(newData);
  };

  // Remove repeatable item
  const removeRepeatableItem = (sectionId, index) => {
    const items = repeatableItems[sectionId].filter((_, i) => i !== index);
    setRepeatableItems({ ...repeatableItems, [sectionId]: items.length ? items : [{}] });
    const newData = { ...formData, [sectionId]: items.length ? items : [{}] };
    setFormData(newData);
  };

  // Navigate sections
  const goToSection = (index) => {
    saveProgress(formData, index);
    setCurrentSectionIndex(index);
  };

  const nextSection = () => {
    if (currentSectionIndex < selectedTemplate.sections.length - 1) {
      goToSection(currentSectionIndex + 1);
    }
  };

  const prevSection = () => {
    if (currentSectionIndex > 0) {
      goToSection(currentSectionIndex - 1);
    }
  };

  // Complete session
  const completeSession = async () => {
    try {
      await axios.patch(`${API}/guided-sessions/${session.id}`, {
        status: 'completed',
        data: formData
      });
      toast.success('Template completed! Your responses have been saved.');
      onClose();
    } catch (error) {
      toast.error('Failed to complete session');
    }
  };

  // Ask coach for help
  const askForHelp = (field) => {
    const question = `I'm working on the "${selectedTemplate.name}" template. I'm stuck on "${field.label}". Can you help me understand what to put here?`;
    onAskCoach(question);
    onClose();
  };

  // Calculate progress
  const progress = selectedTemplate 
    ? ((currentSectionIndex + 1) / selectedTemplate.sections.length) * 100 
    : 0;

  // Render field
  const renderField = (field, sectionId, itemIndex = null) => {
    const getValue = () => {
      if (itemIndex !== null) {
        return repeatableItems[sectionId]?.[itemIndex]?.[field.id] || '';
      }
      return formData[sectionId]?.[field.id] || '';
    };

    const handleChange = (value) => {
      handleFieldChange(sectionId, field.id, value, itemIndex);
    };

    return (
      <div key={field.id} className="space-y-2">
        <div className="flex items-start justify-between">
          <Label className="text-sm font-medium text-slate-700">
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </Label>
          <button
            onClick={() => askForHelp(field)}
            className="text-slate-400 hover:text-amber-600 p-1"
            title="Ask coach for help"
          >
            <HelpCircle className="h-4 w-4" />
          </button>
        </div>
        
        {field.help && (
          <p className="text-xs text-slate-500">{field.help}</p>
        )}

        {field.type === 'text' && (
          <Input
            value={getValue()}
            onChange={(e) => handleChange(e.target.value)}
            placeholder={field.placeholder}
            className="border-slate-200"
          />
        )}

        {field.type === 'textarea' && (
          <Textarea
            value={getValue()}
            onChange={(e) => handleChange(e.target.value)}
            placeholder={field.placeholder}
            rows={3}
            className="border-slate-200 resize-none"
          />
        )}

        {field.type === 'rating' && (
          <RadioGroup
            value={getValue()?.toString()}
            onValueChange={(val) => handleChange(parseInt(val))}
            className="space-y-2"
          >
            {field.options.map((option) => (
              <div key={option.value} className="flex items-center space-x-2">
                <RadioGroupItem value={option.value.toString()} id={`${field.id}-${option.value}-${itemIndex}`} />
                <Label 
                  htmlFor={`${field.id}-${option.value}-${itemIndex}`}
                  className="text-sm text-slate-600 cursor-pointer"
                >
                  <span className="font-medium">{option.value}</span> - {option.label}
                </Label>
              </div>
            ))}
          </RadioGroup>
        )}
      </div>
    );
  };

  // Template selection view
  if (!selectedTemplate) {
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <motion.div 
          className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <div className="p-6 border-b border-slate-200">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-slate-900">Start Guided Mode</h2>
                <p className="text-sm text-slate-500 mt-1">Choose a template to work through step by step</p>
              </div>
              <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
          
          <div className="p-6 space-y-4">
            {loading ? (
              <div className="text-center py-8">
                <div className="spinner mx-auto" />
              </div>
            ) : (
              templates.map((template) => {
                const Icon = TEMPLATE_ICONS[template.id] || FileText;
                return (
                  <button
                    key={template.id}
                    onClick={() => startSession(template.id)}
                    className="w-full p-4 border border-slate-200 rounded-lg hover:border-amber-300 hover:bg-amber-50/50 transition-colors text-left flex items-start gap-4"
                  >
                    <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon className="h-5 w-5 text-slate-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">{template.name}</h3>
                      <p className="text-sm text-slate-500 mt-0.5">{template.description}</p>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </motion.div>
      </div>
    );
  }

  // Guided session view
  const currentSection = selectedTemplate.sections[currentSectionIndex];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <motion.div 
        className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        {/* Header */}
        <div className="p-4 border-b border-slate-200 flex-shrink-0">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-lg font-bold text-slate-900">{selectedTemplate.name}</h2>
              <p className="text-sm text-slate-500">Section {currentSectionIndex + 1} of {selectedTemplate.sections.length}</p>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
              <X className="h-5 w-5" />
            </button>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Section navigation pills */}
        <div className="px-4 py-3 border-b border-slate-100 flex-shrink-0 overflow-x-auto">
          <div className="flex gap-2">
            {selectedTemplate.sections.map((section, index) => (
              <button
                key={section.id}
                onClick={() => goToSection(index)}
                className={`px-3 py-1.5 text-sm rounded-full whitespace-nowrap transition-colors ${
                  index === currentSectionIndex
                    ? 'bg-slate-900 text-white'
                    : index < currentSectionIndex
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {index < currentSectionIndex && <CheckCircle className="h-3 w-3 inline mr-1" />}
                {section.name}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentSection.id}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div>
                <h3 className="text-lg font-semibold text-slate-900">{currentSection.name}</h3>
                {currentSection.description && (
                  <p className="text-sm text-slate-500 mt-1">{currentSection.description}</p>
                )}
              </div>

              {currentSection.repeatable ? (
                <div className="space-y-6">
                  {(repeatableItems[currentSection.id] || [{}]).map((item, itemIndex) => (
                    <div key={itemIndex} className="p-4 border border-slate-200 rounded-lg space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-slate-700">Item {itemIndex + 1}</span>
                        {repeatableItems[currentSection.id]?.length > 1 && (
                          <button
                            onClick={() => removeRepeatableItem(currentSection.id, itemIndex)}
                            className="text-red-500 hover:text-red-700 p-1"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                      {currentSection.fields.map((field) => renderField(field, currentSection.id, itemIndex))}
                    </div>
                  ))}
                  <Button
                    variant="outline"
                    onClick={() => addRepeatableItem(currentSection.id)}
                    className="w-full"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Another
                  </Button>
                </div>
              ) : currentSection.auto_generated ? (
                <div className="text-center py-8 text-slate-500">
                  <p>This section will be auto-generated based on your inputs.</p>
                  <p className="text-sm mt-2">Complete the previous sections first.</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {currentSection.fields.map((field) => renderField(field, currentSection.id))}
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-200 flex items-center justify-between flex-shrink-0">
          <Button
            variant="outline"
            onClick={prevSection}
            disabled={currentSectionIndex === 0}
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          
          <span className="text-sm text-slate-500">
            {saving ? 'Saving...' : 'Auto-saved'}
          </span>
          
          {currentSectionIndex === selectedTemplate.sections.length - 1 ? (
            <Button onClick={completeSession} className="bg-emerald-600 hover:bg-emerald-700">
              <CheckCircle className="h-4 w-4 mr-2" />
              Complete
            </Button>
          ) : (
            <Button onClick={nextSection}>
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          )}
        </div>
      </motion.div>
    </div>
  );
}
